import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Fingerprint, X } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api'; // Use relative URL for same-origin API calls

export default function BiometricEnrollPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;

    const checkEnrollment = async () => {
      // Check basic WebAuthn support
      if (!window.PublicKeyCredential) return;

      // Don't show if dismissed recently (7 days)
      const dismissed = localStorage.getItem('biometric_prompt_dismissed');
      if (dismissed && Date.now() - parseInt(dismissed) < 7 * 86400000) return;

      // Check if user already has credentials
      try {
        const { data } = await axios.get(`${API}/webauthn/credentials`);
        if (data.length > 0) return; // Already enrolled
      } catch {
        return;
      }

      // Show prompt on mobile/tablet devices
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (!isMobile) {
        // On desktop, only show if platform authenticator confirmed available
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          if (!available) return;
        } catch {
          return;
        }
      }

      setTimeout(() => setShow(true), 4000);
    };

    checkEnrollment();
  }, [user]);

  const handleEnroll = async () => {
    try {
      const { data } = await axios.get(`${API}/webauthn/register/options`);
      const optionsJSON = typeof data.options === 'string' ? JSON.parse(data.options) : data.options;
      const sessionId = data.session_id;

      const credential = await startRegistration({ optionsJSON });

      await axios.post(`${API}/webauthn/register/verify?session_id=${encodeURIComponent(sessionId)}`, credential);
      
      // Store email for biometric login
      if (user?.email) {
        localStorage.setItem('biometric_email', user.email);
      }
      
      toast.success('Biometric login enabled! You can now sign in with your fingerprint or face.');
      setShow(false);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric setup was cancelled');
        // Dismiss for 7 days so it doesn't keep nagging
        localStorage.setItem('biometric_prompt_dismissed', Date.now().toString());
        setShow(false);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to set up biometric login');
        localStorage.setItem('biometric_prompt_dismissed', Date.now().toString());
        setShow(false);
      }
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('biometric_prompt_dismissed', Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 sm:left-auto sm:right-6 sm:max-w-sm animate-in slide-in-from-bottom-4 duration-300" data-testid="biometric-enroll-prompt">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Fingerprint className="w-5 h-5 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900">Enable Biometric Login</p>
            <p className="text-xs text-gray-500 mt-0.5">Skip your password next time — log in with just your face or fingerprint.</p>
          </div>
          <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={handleEnroll}
            className="flex-1 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium py-2.5 rounded-xl transition-colors"
            data-testid="enable-biometric-btn"
          >
            Enable
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="dismiss-biometric-btn"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
